/**
 * What the Intercom card shares with machine: the message its OAuth popup posts back, and where
 * the workspace id sits in the stored credentials. No imports, so the node test runner can load it.
 */
export const INTERCOM_OAUTH_MESSAGE_SOURCE = 'kim-intercom-oauth';

export const INTERCOM_DEFAULT_ERROR_MESSAGE = 'Intercom connection failed. Please try again.';

export interface IntercomOAuthMessage {
	source: typeof INTERCOM_OAUTH_MESSAGE_SOURCE;
	status: 'success' | 'error';
	message?: string;
}

/** Parse an incoming `message` payload; anything that is not ours is `null`. */
export const readIntercomOAuthMessage = (data: unknown): IntercomOAuthMessage | null => {
	if (typeof data !== 'object' || data === null) return null;
	const candidate = data as Record<string, unknown>;
	if (candidate.source !== INTERCOM_OAUTH_MESSAGE_SOURCE) return null;
	if (candidate.status !== 'success' && candidate.status !== 'error') return null;

	return {
		source: INTERCOM_OAUTH_MESSAGE_SOURCE,
		status: candidate.status,
		message: typeof candidate.message === 'string' ? candidate.message : undefined
	};
};

const asNonEmptyString = (value: unknown): string | undefined => {
	if (typeof value === 'number' && Number.isFinite(value)) return String(value);
	return typeof value === 'string' && value.trim() ? value : undefined;
};

/**
 * The Intercom workspace id for the card's "connected" label. Credentials are keyed `intercom`
 * in `auth_meta_data`; the flat shape is read too so a differently shaped row still shows it.
 */
export const readIntercomWorkspaceId = (authMetaData: Record<string, unknown> | null | undefined): string | undefined => {
	if (!authMetaData) return undefined;
	const nested = authMetaData.intercom;
	if (nested && typeof nested === 'object') {
		const nestedId = asNonEmptyString((nested as Record<string, unknown>).app_id);
		if (nestedId) return nestedId;
	}
	return asNonEmptyString(authMetaData.app_id);
};
